import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, FileDown, Printer } from "lucide-react";
import { useLocations } from "@/hooks/useLocations";
import { useAssets } from "@/hooks/useAssets";
import { MASTER_CATEGORIES } from "@/lib/categories";
import { toast } from "@/hooks/use-toast";
import { useCountry } from "@/hooks/useCountry";
import { formatCurrency } from "@/lib/countries";
import { generateInsuranceCSV, downloadCSV } from "@/lib/generateCSV";
import { track } from "@/lib/analytics";

type Scope = "full" | "location" | "category" | "custom";
type ExportFormat = "report" | "csv";

interface InsuranceExportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preSelectedIds?: Set<string>;
}

export default function InsuranceExportSheet({ open, onOpenChange, preSelectedIds }: InsuranceExportSheetProps) {
  const navigate = useNavigate();
  const { data: locations } = useLocations();
  const { data: allAssets } = useAssets();
  const { countryCode } = useCountry();

  const [exportFormat, setExportFormat] = useState<ExportFormat>("report");
  const [scope, setScope] = useState<Scope>(preSelectedIds && preSelectedIds.size > 0 ? "custom" : "full");

  useEffect(() => {
    if (open && preSelectedIds && preSelectedIds.size > 0) {
      setScope("custom");
    } else if (open && (!preSelectedIds || preSelectedIds.size === 0)) {
      setScope("full");
    }
  }, [open, preSelectedIds]);

  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());

  // Report-only fields
  const [includePhotos, setIncludePhotos] = useState(true);
  const [ownerName, setOwnerName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");

  const [generating, setGenerating] = useState(false);

  const assetCategories = useMemo(() => {
    const cats = new Set((allAssets ?? []).map((a) => a.category));
    return MASTER_CATEGORIES.filter((c) => cats.has(c.value));
  }, [allAssets]);

  const scopedAssetIds = useMemo(() => {
    if (!allAssets) return [];
    let filtered = allAssets;
    switch (scope) {
      case "location":
        if (selectedLocations.size > 0) {
          filtered = filtered.filter((a) => a.location_id && selectedLocations.has(a.location_id));
        }
        break;
      case "category":
        if (selectedCategories.size > 0) {
          filtered = filtered.filter((a) => selectedCategories.has(a.category));
        }
        break;
      case "custom":
        if (preSelectedIds && preSelectedIds.size > 0) {
          filtered = filtered.filter((a) => preSelectedIds.has(a.id));
        }
        break;
    }
    return filtered.map((a) => a.id);
  }, [allAssets, scope, selectedLocations, selectedCategories, preSelectedIds]);

  const totalValue = useMemo(() => {
    if (!allAssets) return 0;
    return allAssets
      .filter((a) => scopedAssetIds.includes(a.id))
      .reduce((sum, a) => sum + ((a.purchase_price ?? a.current_estimated_value ?? 0) * (a.quantity ?? 1)), 0);
  }, [allAssets, scopedAssetIds]);

  const isPrintMode = exportFormat === "report";

  // For CSV: always export full inventory; for report: use scope
  const exportAssetCount = isPrintMode ? scopedAssetIds.length : (allAssets?.length ?? 0);
  const exportValue = isPrintMode ? totalValue : (allAssets ?? []).reduce(
    (sum, a) => sum + ((a.purchase_price ?? a.current_estimated_value ?? 0) * (a.quantity ?? 1)), 0
  );

  const handleGenerate = async () => {
    if (exportAssetCount === 0) {
      toast({ title: "No assets to export", variant: "destructive" });
      return;
    }

    if (exportFormat === "report") {
      track("insurance_report_exported", { format: "report", asset_count: exportAssetCount });
      navigate("/report/print", {
        state: {
          assetIds: scopedAssetIds,
          ownerName,
          propertyAddress,
          includePhotos,
        },
      });
      onOpenChange(false);
      return;
    }

    setGenerating(true);
    try {
      const csvAssets = allAssets ?? [];
      const csv = generateInsuranceCSV(csvAssets);
      const today = new Date().toISOString().slice(0, 10);
      downloadCSV(csv, `manifest-inventory-${today}.csv`);
      track("insurance_report_exported", { format: "csv", asset_count: csvAssets.length });
      toast({ title: "⬇️ CSV downloaded", description: `${csvAssets.length} assets included` });
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "CSV export failed";
      toast({ title: "Export failed", description: msg, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const toggleSet = (set: Set<string>, id: string): Set<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh] !z-[70]">
        <DrawerHeader className="flex items-center justify-between border-b border-border pb-3">
          <div>
            <DrawerTitle className="font-display text-xl">Export Inventory</DrawerTitle>
            <p className="text-sm text-muted-foreground mt-0.5">
              {isPrintMode
                ? "Generate a claim-ready report to print or save as PDF"
                : "Download a spreadsheet of your inventory"}
            </p>
          </div>
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
              <X className="h-5 w-5" />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <ScrollArea className="flex-1 px-4 overflow-y-auto" style={{ maxHeight: "calc(90vh - 180px)" }}>
          <div className="space-y-6 py-4">
            {/* ── Export Format ───────────────────────────────── */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Export Format</h3>
              <div className="space-y-2">
                {([
                  {
                    value: "report" as ExportFormat,
                    label: "PDF / Print Report",
                    desc: "Claim-ready report with photos, valuations, and depreciation — print or save as PDF",
                  },
                  {
                    value: "csv" as ExportFormat,
                    label: "CSV Spreadsheet",
                    desc: "All columns included — import into adjuster tools or spreadsheet apps",
                  },
                ] as { value: ExportFormat; label: string; desc: string }[]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setExportFormat(opt.value)}
                    className="flex items-start gap-3 cursor-pointer w-full text-left min-h-[44px]"
                  >
                    <div
                      className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        exportFormat === opt.value ? "border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {exportFormat === opt.value && (
                        <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                      )}
                    </div>
                    <div>
                      <span className="text-sm text-foreground font-medium">{opt.label}</span>
                      <p className="text-xs text-muted-foreground">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── Scope ───────────────────────────────────────── */}
            {isPrintMode ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Scope</h3>
              <div className="space-y-2">
                {([
                  { value: "full" as Scope, label: "Full inventory" },
                  { value: "location" as Scope, label: "By location" },
                  { value: "category" as Scope, label: "By category" },
                  ...(preSelectedIds && preSelectedIds.size > 0
                    ? [{ value: "custom" as Scope, label: `Custom selection (${preSelectedIds.size})` }]
                    : []),
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setScope(opt.value)}
                    className="flex items-center gap-3 cursor-pointer w-full text-left min-h-[44px]"
                  >
                    <div
                      className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                        scope === opt.value ? "border-primary" : "border-muted-foreground/40"
                      }`}
                    >
                      {scope === opt.value && <div className="h-2.5 w-2.5 rounded-full bg-primary" />}
                    </div>
                    <span className="text-sm text-foreground">{opt.label}</span>
                  </button>
                ))}
              </div>

              {scope === "location" && (
                <div className="flex flex-wrap gap-2 pl-7">
                  {(locations ?? []).map((loc) => (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => setSelectedLocations((prev) => toggleSet(prev, loc.id))}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        selectedLocations.has(loc.id)
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
              )}

              {scope === "category" && (
                <div className="flex flex-wrap gap-2 pl-7">
                  {assetCategories.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setSelectedCategories((prev) => toggleSet(prev, cat.value))}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        selectedCategories.has(cat.value)
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            ) : (
            <div className="rounded-lg border border-border bg-secondary/20 px-4 py-3 text-sm text-muted-foreground">
              CSV export includes your <span className="font-medium text-foreground">full inventory</span> for spreadsheet and import use.
            </div>
            )}

            {/* ── Report Details — PDF/Print only ─────────────── */}
            {isPrintMode && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Report Details{" "}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="owner_name" className="text-xs">Owner Name</Label>
                    <Input
                      id="owner_name"
                      value={ownerName}
                      onChange={(e) => setOwnerName(e.target.value)}
                      placeholder="Your name"
                      className="min-h-[40px]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="property_address" className="text-xs">Property Address</Label>
                    <Input
                      id="property_address"
                      value={propertyAddress}
                      onChange={(e) => setPropertyAddress(e.target.value)}
                      placeholder="123 Main St, City, State"
                      className="min-h-[40px]"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── Details to Include — PDF/Print only ─────────── */}
            {isPrintMode && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Details to Include</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground">Photos</span>
                  <Switch checked={includePhotos} onCheckedChange={setIncludePhotos} />
                </div>
              </div>
            )}

          </div>
        </ScrollArea>

        <DrawerFooter className="border-t border-border pt-3">
          <p className="text-xs text-muted-foreground text-center mb-1">
            {exportAssetCount} assets · {formatCurrency(exportValue, countryCode)} total value
          </p>
          <Button
            onClick={handleGenerate}
            disabled={generating || exportAssetCount === 0}
            className="w-full min-h-[48px] gap-2 font-semibold"
          >
            {generating ? (
              <>
                <FileDown className="h-4 w-4 animate-bounce" />
                Exporting…
              </>
            ) : isPrintMode ? (
              <>
                <Printer className="h-4 w-4" />
                Print Report
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4" />
                Download CSV
              </>
            )}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
